<template>
  <b-modal :id="modalId" :title="modalTitle" @hide="onHide" hide-footer body-class="p-0" title-class="modal-title-white" size="lg">
    <div class="p-3 body-flex">
      <!-- Side-by-side dropdowns -->
      <div class="row mb-4">
        <div class="col-sm-6">
          <label class="form-label fw-bold mb-2">Board Title</label>
          <input v-model="localColumn.title" type="text" class="form-control" placeholder="Enter board title" />
        </div>
        <div class="col-sm-6">
          <label class="form-label fw-bold mb-2">Default Status - <small class="text-muted">Assigned when jobs are dragged</small></label>
          <select v-model="localColumn.defaultStatusId" class="form-control">
            <option value="">Select a default status...</option>
            <option 
              v-for="status in statusOptions" 
              :key="status.id" 
              :value="status.id"
            >
              {{ status.displayName }}
            </option>
          </select>
        </div>
      </div>
      <div class="mb-0 d-flex flex-column flex-grow-1">
        <div class="mb-3 instruction-highlight p-2">
          Select statuses to display in this board. Each status can only belong to one column. Used statuses are disabled.
          <small class="d-block mt-1 text-muted font-italic">Changes save automatically when you close this window.</small>
        </div>
        <div class="status-scroll flex-grow-1">
          <div class="status-checkbox-list border rounded" v-if="statusOptions.length">
            <div
              v-for="status in statusOptions"
              :key="status.id"
              class="status-item d-flex align-items-center py-2 px-3"
              :class="{ 'used': isStatusUsedElsewhere(status.id) }"
            >
              <input
                class="custom-checkbox"
                type="checkbox"
                :id="`status-${status.id}`"
                :value="status.id"
                :disabled="isStatusUsedElsewhere(status.id)"
                v-model="selectedStatusIds"
              />
              <label class="flex-grow-1 mb-0 status-label" :for="`status-${status.id}`">
                <span :class="{ 'text-muted used-text': isStatusUsedElsewhere(status.id) }">
                  {{ status.displayName }}
                </span>
                <span v-if="getStatusAssignment(status.id)" class="assignment-indicator">
                  ({{ getStatusAssignment(status.id).boardTitle }} - {{ getStatusAssignment(status.id).columnTitle }})
                </span>
              </label>
            </div>
          </div>
          <div v-else class="text-muted small py-2">Loading statuses...</div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import GlobalServices from '@/services/global';
import MODULE from '@/constants/modules';

const global = new GlobalServices();

export default {
  name: 'BoardColumnSettingsModal',
  props: {
    modalId: { type: String, required: true },
    column: { type: Object, required: true },
    boardType: { type: String, required: true }, // New prop to identify which board this column belongs to
    usedStatusIds: { type: Array, default: () => [] } // Legacy prop - may be removed in future
  },
  data() {
    return {
      localColumn: { ...this.column },
      statusOptions: [],
      selectedStatusIds: [],
      loading: false
    };
  },
  computed: {
    ...mapGetters('boardSettings', ['getStatusAssignment', 'getAllUsedStatusIds']),
    
    originalTitle() {
      return this.column ? this.column.title : '';
    },
    modalTitle() {
      return this.originalTitle ? `Edit Board Status - ${this.originalTitle}` : 'Edit Board Status';
    }
  },
  watch: {
    column: {
      immediate: true,
      handler(newVal) {
        this.localColumn = { ...newVal };
        this.selectedStatusIds = [...(newVal.statusIds || [])];
      }
    },
    'localColumn.defaultStatusId': {
      handler(newStatusId) {
        // Update defaultStatusName when defaultStatusId changes
        if (newStatusId && this.statusOptions.length > 0) {
          const status = this.statusOptions.find(s => s.id === newStatusId);
          if (status) {
            this.localColumn.defaultStatusName = status.displayName;
          }
        } else {
          this.localColumn.defaultStatusName = '';
        }
      }
    }
  },
  methods: {
    async loadStatusOptions() {
      try {
        this.loading = true;
        // Use existing UX dropdown for project statuses
        const opts = await global.getUXDropDown(MODULE.PROJECTS.Status);
        this.statusOptions = opts;
        // Map existing IDs
        this.selectedStatusIds = [...(this.column.statusIds || [])];
        
        // Set defaultStatusId if defaultStatusName is already set but ID is missing
        if (this.localColumn.defaultStatusName && !this.localColumn.defaultStatusId) {
          const matchingStatus = opts.find(status => 
            status.displayName.toLowerCase() === this.localColumn.defaultStatusName.toLowerCase()
          );
          if (matchingStatus) {
            this.localColumn.defaultStatusId = matchingStatus.id;
          }
        }
      } catch (e) {
        // Toast handled by parent if needed
      } finally {
        this.loading = false;
      }
    },
    show() {
      this.$bvModal.show(this.modalId);
      if (!this.statusOptions.length) {
        this.loadStatusOptions();
      }
    },
    onSave(evt) {
      // Emit updated config with board type for centralized store
      const statusIds = [...this.selectedStatusIds];
      this.$emit('save', { 
        boardType: this.boardType,
        columnId: this.column.id,
        column: { ...this.localColumn, statusIds }
      });
    },
    onHide() {
      // Auto-save on close since footer buttons removed
      const statusIds = [...this.selectedStatusIds];
      this.$emit('save', { 
        boardType: this.boardType,
        columnId: this.column.id,
        column: { ...this.localColumn, statusIds }
      });
      this.$emit('closed');
    },
    isStatusUsed(id) {
      // Legacy method - use isStatusUsedElsewhere instead
      return this.usedStatusIds.includes(id);
    },
    isStatusUsedElsewhere(id) {
      // Check if status is used in another board/column (not current selection)
      if (this.selectedStatusIds.includes(id)) {
        return false; // Currently selected in this column, so not "elsewhere"
      }
      
      const assignment = this.getStatusAssignment(id);
      if (!assignment) {
        return false; // Not assigned anywhere
      }
      
      // Check if it's assigned to a different board or different column in same board
      return !(assignment.boardType === this.boardType && assignment.columnTitle === this.column.title);
    }
  }
};
</script>

<style scoped>

.instruction-highlight {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 0.95rem;
  line-height: 1.4;
  color: #495057;
  font-weight: 500;
}

.status-checkbox-list { 
  max-height: 35vh; 
  overflow-y: auto; 
  background-color: white;
}

.body-flex { 
  display: flex; 
  flex-direction: column; 
  max-height: 75vh; /* Increased for larger modal */
}

.status-scroll { 
  overflow-y: auto; 
  min-height: 0; /* flexbox scroll fix */
}

.status-item { 
  border-bottom: 1px solid #f2f2f2; 
  min-height: 45px; /* Reduced height to fit more items */
  transition: background-color 0.15s ease;
}

.status-item:last-child { 
  border-bottom: none; 
}

.status-item:hover:not(.used) {
  background-color: #f8f9fa;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #6e0270;
  border-radius: 4px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: white;
  position: relative;
  flex-shrink: 0;
  margin-right: 12px; /* space between checkbox & label */
}

.custom-checkbox:checked {
  background-color: #6e0270;
  border-color: #6e0270;
}

.custom-checkbox:checked::after {
  content: '✓';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
  font-size: 14px;
}

.custom-checkbox:disabled {
  border-color: #ccc;
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.status-label { 
  font-size: 1rem; 
  cursor: pointer;
  font-weight: 500;
}

.status-item.used .status-label .used-text { 
  text-decoration: line-through;
  text-decoration-color: #6e0270;
  text-decoration-thickness: 2px;
}

.status-item.used .custom-checkbox {
  opacity: 0.5;
}

.assignment-indicator {
  font-size: 0.875rem;
  color: #6e0270;
  font-weight: 500;
  margin-left: 8px;
}

.status-item.used .assignment-indicator {
  color: #999;
}

/* Footer removed; related styles deleted */

/* Responsive adjustments for modal content */
@media (max-width: 768px) {
  .body-flex {
    max-height: 80vh;
  }
  
  .status-checkbox-list {
    max-height: 40vh;
  }
  
  .status-item {
    min-height: 50px; /* Slightly larger touch targets on mobile */
  }
}

@media (max-width: 576px) {
  .body-flex {
    max-height: 85vh;
  }
  
  .row.mb-4 .col-sm-6:first-child {
    margin-bottom: 1rem;
  }
}
</style>

<!-- Non-scoped style needed because BootstrapVue modal is appended to <body> -->
<style>
.modal-title-white { color: #fff !important; }
</style>
